import { useEffect, useState } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { returnIsValid } from "../../Main/useConstants";
import { DragAndDropFiles, InvestigationButton, ModalContainer, TemporalUploadedFile } from "../../components"
import { createAttachment } from "../../../../../../apis";
import { RichTextEditor } from "../../../../../MainComponents/RichTextEditor/RichTextEditor";

const validateForm = (files, description) => {
  const errors = {};

  if (!Array.isArray(files) || files.length === 0) errors.files = true;

  const text = description.replace(/<[^>]*>/g, "").trim();
  if (text.length === 0) {
    errors.description = true;
  }

  return [
    Object.entries(errors).length === 0 ? true : false,
    errors
  ];
};

export const AddFiles = ({
  show,
  onHide,
  plans,
  selectedPlan,
  setSelectedPlan,
  reloadInvestigation,
  handleSavePlans
}) => { 

  const theme = useTheme();
  const [t] = useTranslation("investigation");

  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState("");
  // const [details, setDetails] = useState("");
  
  const [errors, setErrors] = useState({});
  const [triedToSave, setTriedToSave] = useState(false);

  // Reset values when modal is closed
  useEffect(() => {
    if (!show) {
      resetValues();
    }
  }, [show]);

  // Validate form on every change
  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm(files, description);
      setErrors(errors);
    }
  }, [files, description]);

  const handleFileSelect = (evtFiles) => {
    const newFiles = [...files, ...evtFiles];
    setFiles(newFiles);
  }

  const handleClose = () => {
    resetValues();
    onHide();
  }

  const handleSave = async () => {
    const [isValid, errors] = validateForm(files, description);
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      enqueueSnackbar({ variant: "error", message: t("resources.missing_required_inputs") });
      return;
    };
    
    let filesToSave;
    
    try {
      if (files.length > 0) {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append("attachments", file);
        });
        const resp = await createAttachment(formData);
        const attachments_id = resp.attachments_id;
        filesToSave = attachments_id.map((id, idx) => ({
          file: id,
          name: files[idx].name,
          size: files[idx].size,
          type: files[idx].type,
          description,
        }));
      }

      const currentFiles = Array.isArray(plans[selectedPlan].files) ? plans[selectedPlan].files : [];

      const newPlans = _.cloneDeep(plans);
      newPlans[selectedPlan].files = [...currentFiles, ...filesToSave];
      await handleSavePlans(newPlans);
      reloadInvestigation();
      handleClose();
    } catch (error) {
      console.log(error);
    }
  }

  const resetValues = () => {
    setFiles([]);
    setErrors({});
    setTriedToSave(false);
    setSelectedPlan(null);
  }

  return (
    <ModalContainer show={show} onHide={onHide}>
      <Stack spacing={2}>
        {/* Header */}
        <Box >
          <Typography fontWeight={700} fontSize={28} color={theme.palette.ethicsBlue.main}>
            {t("activityPlan.upload_files_title")}
          </Typography>
          <Typography fontSize={16} color="oxfordBlue" >
            {t("activityPlan.upload_files_subtitle")}
          </Typography>

          <hr style={{ marginBottom: 0 }} />
        </Box>

        <DragAndDropFiles
          text={t("activityPlan.drag_and_drop")}
          onFileSelect={handleFileSelect}
          multiple
          isValid={returnIsValid(errors?.files, triedToSave) ?? null}
        />

        <RichTextEditor
          label={t("activityPlan.description")}
          value={description}
          onChange={(value) => setDescription(value)}
          isValid={returnIsValid(errors?.description, triedToSave) ?? null}
        />

        {
          files.length > 0 && (
            <Grid2  d2 container spacing={2}>
              {
                files.map((file, index) => (
                  <Grid2 key={index} size={{ xs: 12, md: 6 }}>
                    <TemporalUploadedFile
                      name={file.name}
                      size={file.size}
                      iconName={`${file != null ? "trash_can" : ""}`}
                      iconColor={theme.palette.red.main}
                      iconAction={() => setFiles(files.filter((_, i) => i !== index))}
                      width="100%"
                    />
                  </Grid2>
                ))
              }
            </Grid2>
          )
        }


        {/* Botones */}
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <InvestigationButton
            label={t("activityPlan.cancel")}
            variant="main"
            outlined
            onClick={handleClose}
          />
          <InvestigationButton
            label={t("activityPlan.save")}
            variant="main"
            onClick={handleSave}
          />
        </Stack>
      </Stack>

    </ModalContainer>
  )
}
